// src/components/ExamPart.jsx
// Jeden komponent dla wszystkich dziewięciu zestawów pytań egzaminacyjnych.
// Wcześniej każdy zestaw był osobnym plikiem z treścią zaszytą w JSX - ten sam
// układ powielony dziewięć razy, bez danych strukturalnych i z ozdobnymi emoji,
// które w wielu przeglądarkach renderowały się jako puste prostokąty.
import React from 'react';
import { Link } from 'react-router-dom';
import ExamNav, { ExamBreadcrumbs } from './ExamNav';
import JsonLd from '../seo/JsonLd';
import { faqJsonLd, getLowestPrice } from '../seo/siteMeta';
import { getExamPart, countQuestions, TOTAL_PARTS } from '../data/examParts';
import { questionPath } from '../data/examQuestions';
import { trackEvent } from '../lib/analytics';
import styles from '../css/modules/ExamQuestions.module.css';

/** Odpowiedź jako jeden ciąg tekstu - potrzebna do danych strukturalnych. */
const answerText = blocks =>
  blocks
    .map(block => (block.type === 'ul' ? block.items.join(' ') : block.text))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

const AnswerBlocks = ({ blocks }) => (
  <>
    {blocks.map((block, index) =>
      block.type === 'ul' ? (
        <ul key={index} className={styles.answerList}>
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      ) : (
        <p key={index}>{block.text}</p>
      )
    )}
  </>
);

const ExamPart = ({ path }) => {
  const part = getExamPart(path);
  if (!part) return null;

  const questionCount = countQuestions(part);
  const lowestPrice = getLowestPrice();

  // Numeracja ciągła w obrębie części - czytelnik wie, ile już przerobił.
  let counter = 0;

  const faqItems = part.sections.flatMap(section =>
    section.questions.map(question => ({
      question: question.q,
      answer: answerText(question.blocks),
    }))
  );

  return (
    <div className={styles.page}>
      <JsonLd data={faqJsonLd(faqItems)} />

      <div className={styles.inner}>
        <ExamBreadcrumbs current={part.path} />

        <header className={styles.hero}>
          <p className={styles.eyebrow}>
            Część {part.part} z {TOTAL_PARTS} · {questionCount} pytań
          </p>
          <h1 className={styles.h1}>{part.h1}</h1>
          <p className={styles.lead}>{part.lead}</p>

          <div className={styles.ctaRow}>
            <Link
              to="/intro"
              className={styles.primaryButton}
              onClick={() => trackEvent('cta_click', { location: 'exam_hero', label: 'demo' })}
            >
              Rozwiąż darmowy test
            </Link>
            <Link
              to="/cennik"
              className={styles.secondaryButton}
              onClick={() => trackEvent('cta_click', { location: 'exam_hero', label: 'cennik' })}
            >
              Pełny dostęp od {lowestPrice} zł
            </Link>
          </div>

          <p className={styles.sourceNote}>
            Pytania odpowiadają zakresowi egzaminu teoretycznego PZSS. Odpowiedzi są
            autorskim opracowaniem redakcji i mają charakter edukacyjny - przed egzaminem
            sprawdź aktualne brzmienie przepisów.
          </p>
        </header>

        {part.sections.length > 1 && (
          <nav className={styles.toc} aria-label="Spis zagadnień">
            <p className={styles.tocTitle}>W tej części</p>
            <ul>
              {part.sections.map(section => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>
                    {section.heading}
                    <span className={styles.tocCount}>{section.questions.length}</span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        )}

        {part.sections.map(section => (
          <section key={section.id} id={section.id} className={styles.section}>
            <h2 className={styles.h2}>{section.heading}</h2>

            <div className={styles.questions}>
              {section.questions.map(question => {
                counter += 1;
                return (
                  <article key={question.slug} className={styles.card}>
                    <h3 className={styles.question}>
                      <span className={styles.number} aria-hidden="true">
                        {counter}
                      </span>
                      {/* Tytuł prowadzi do podstrony z rozszerzonym omówieniem. */}
                      <Link to={questionPath(question.slug)} className={styles.questionLink}>
                        {question.q}
                      </Link>
                    </h3>
                    <div className={styles.answer}>
                      <AnswerBlocks blocks={question.blocks} />
                      <Link to={questionPath(question.slug)} className={styles.more}>
                        Szersze omówienie i najczęstszy błąd →
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))}

        <section className={styles.summary}>
          <h2 className={styles.h2}>Co dalej</h2>
          <p>{part.summary}</p>
          {part.related.length > 0 && (
            <ul className={styles.relatedList}>
              {part.related.map(link => (
                <li key={link.to || link.href}>
                  {link.to ? (
                    <Link to={link.to}>{link.label}</Link>
                  ) : (
                    // Poradniki stoją pod /egzamin (osobna aplikacja na tej samej domenie).
                    <a href={link.href}>{link.label}</a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <ExamNav current={part.path} />
      </div>
    </div>
  );
};

export default ExamPart;
